import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, SPEC_KEYS } from "@/lib/constants";
import { cx } from "@/components/FormFields";
import { Plus, Trash2, X } from "lucide-react";

const PRODUCT_CATS = CATEGORIES.filter((c) => c !== "All");
const empty = { name: "", category: PRODUCT_CATS[0], price: 0, description: "", colors: [], specs: {}, image_url: "", images: [], badge: "", warranty: "", status: "available" };
const STATUSES = [["available", "Available"], ["out_of_stock", "Out of Stock"], ["hidden", "Hidden"]];

export function ProductsPanel({ input }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product form object or null
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const onDrop = async (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const next = [...products];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(toIdx, 0, moved);
    setProducts(next);
    setDragIdx(null);
    try { await api.patch("/products/reorder", { ids: next.map((p) => p.id) }); }
    catch { toast.error("Could not save new order."); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data);
    } catch {
      toast.error("Failed to load products.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ ...empty, colors: [], specs: {} });
  const openEdit = (p) => setEditing({ ...p, colors: [...(p.colors || [])], specs: { ...(p.specs || {}) } });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    const body = {
      name: editing.name, category: editing.category, price: Number(editing.price) || 0,
      description: editing.description || "", colors: editing.colors || [],
      specs: Object.fromEntries(Object.entries(editing.specs || {}).filter(([, v]) => String(v).trim())),
      image_url: editing.image_url || "", images: editing.images || [], badge: editing.badge || "", warranty: editing.warranty || "", status: editing.status || "available",
    };
    try {
      if (editing.id) {
        const { data } = await api.put(`/products/${editing.id}`, body);
        setProducts((ps) => ps.map((p) => (p.id === data.id ? data : p)));
        setEditing({ ...data, colors: [...(data.colors || [])], specs: { ...(data.specs || {}) } });
        toast.success("Product updated.");
      } else {
        const { data } = await api.post("/products", body);
        setProducts((ps) => [...ps, data]);
        setEditing({ ...data, colors: [...(data.colors || [])], specs: { ...(data.specs || {}) } });
        toast.success("Product created. You can now upload an image.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      setProducts((ps) => ps.filter((x) => x.id !== p.id));
      if (editing?.id === p.id) setEditing(null);
      toast.success("Product deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editing?.id) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/products/${editing.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((f) => ({ ...f, image_url: data.image_url, images: data.images || [data.image_url] }));
      setProducts((ps) => ps.map((p) => (p.id === editing.id ? { ...p, image_url: (data.images || [data.image_url])[0], images: data.images } : p)));
      toast.success("Image updated.");
    } catch {
      toast.error("Image upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const setField = (k, v) => setEditing((f) => ({ ...f, [k]: v }));
  const setSpec = (k, v) => setEditing((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));
  const addColor = () => setEditing((f) => ({ ...f, colors: [...f.colors, "#8CE63C"] }));
  const setColor = (i, v) => setEditing((f) => ({ ...f, colors: f.colors.map((c, idx) => (idx === i ? v : c)) }));
  const removeColor = (i) => setEditing((f) => ({ ...f, colors: f.colors.filter((_, idx) => idx !== i) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-[#69776b]">Add, edit, and manage every model — pictures, specs, prices, badges, and order. Drag rows to reorder the storefront.</p>
        <button data-testid="product-add-button" onClick={openNew} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold hover:bg-[#9df04f] transition-colors">
          <Plus size={16} /> Add Product
        </button>
      </div>

      <div className="rounded-xl border border-[#232f25] bg-[#111712] overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="admin-products-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#232f25] text-left">
                {["", "Name", "Category", "Price", "Status", ""].map((h, i) => (
                  <th key={i} className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#69776b] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-[#69776b]">Loading…</td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-[#69776b]">No products yet. Click "Add Product".</td></tr>
              ) : (
                products.map((p, i) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)} className="border-b border-[#1a211b] hover:bg-[#0d120e] transition-colors cursor-move">
                    <td className="px-4 py-2">
                      <div className="h-12 w-16 rounded overflow-hidden bg-[#0d120e]">
                        {p.image_url ? <img src={mediaUrl(p.image_url)} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white font-medium whitespace-nowrap">
                      {p.name}
                      {p.badge ? <span className={cx("ml-2 font-mono text-[9px] font-bold uppercase px-1.5 py-0.5 rounded", p.badge === "Best Seller" ? "bg-[#8CE63C] text-[#080b08]" : "bg-[#EA580C] text-white")}>{p.badge}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-[#9EABA0] whitespace-nowrap">{p.category}</td>
                    <td className="px-4 py-3 text-[#8CE63C] font-bold whitespace-nowrap">${p.price}</td>
                    <td className="px-4 py-3">
                      <span className={cx("px-2 py-1 rounded text-[10px] font-semibold border", p.status === "available" ? "bg-[#132717] border-[#8CE63C] text-[#8CE63C]" : p.status === "out_of_stock" ? "bg-[#2A240E] border-[#FACC15] text-[#FDE047]" : "bg-[#261D1D] border-[#F87171] text-[#FCA5A5]")}>
                        {p.status === "out_of_stock" ? "Out of Stock" : p.status === "hidden" ? "Hidden" : "Available"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button data-testid={`product-edit-${p.id}`} onClick={() => openEdit(p)} className="text-xs font-semibold text-[#8CE63C] hover:underline mr-4">Edit</button>
                      <button data-testid={`product-delete-${p.id}`} onClick={() => remove(p)} className="text-xs font-semibold text-[#FCA5A5] hover:underline">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-[#232f25] bg-[#0a0d0a] erp-fade-up" data-testid="product-editor">
            <div className="sticky top-0 bg-[#0a0d0a]/95 backdrop-blur border-b border-[#232f25] px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-display text-xl font-black text-white uppercase tracking-tight">{editing.id ? "Edit Product" : "New Product"}</h2>
              <button data-testid="product-editor-close" onClick={() => setEditing(null)} className="text-[#9EABA0] hover:text-white"><X size={20} /></button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Image */}
              <div className="flex items-center gap-4">
                <div className="h-24 w-32 rounded-lg overflow-hidden bg-[#0d120e] border border-[#232f25] shrink-0">
                  {editing.image_url ? <img src={mediaUrl(editing.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-[#69776b]">No image</div>}
                </div>
                <div className="flex-1">
                  {editing.id ? (
                    <label className={cx("inline-block px-4 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold cursor-pointer hover:bg-[#9df04f] transition-colors", uploading && "opacity-50 pointer-events-none")}>
                      {uploading ? "Uploading…" : "Upload Picture"}
                      <input data-testid="product-image-input" type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                    </label>
                  ) : (
                    <p className="text-xs text-[#69776b]">Save the product first, then upload a picture. You can also paste an image URL below.</p>
                  )}
                  <input value={editing.image_url || ""} onChange={(e) => setField("image_url", e.target.value)} placeholder="or paste image URL (https://…)" className={cx(input, "w-full mt-2")} data-testid="product-image-url" />
                </div>
              </div>

              {editing.images && editing.images.length > 0 ? (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Photos ({editing.images.length}) — first is the main image</label>
                  <div className="flex flex-wrap gap-2" data-testid="product-gallery">
                    {editing.images.map((im, i) => (
                      <div key={i} className="relative group">
                        <img src={mediaUrl(im)} alt="" className="h-16 w-24 object-cover rounded border border-[#232f25]" />
                        <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 bg-black/60 rounded transition-opacity">
                          <button type="button" onClick={() => setEditing((f) => { const a = [...f.images]; if (i > 0) { [a[i - 1], a[i]] = [a[i], a[i - 1]]; } return { ...f, images: a }; })} className="text-white text-sm font-bold">◀</button>
                          <button type="button" data-testid={`product-image-delete-${i}`} onClick={() => setEditing((f) => ({ ...f, images: f.images.filter((_, x) => x !== i) }))} className="text-[#FCA5A5]"><X size={14} /></button>
                          <button type="button" onClick={() => setEditing((f) => { const a = [...f.images]; if (i < a.length - 1) { [a[i + 1], a[i]] = [a[i], a[i + 1]]; } return { ...f, images: a }; })} className="text-white text-sm font-bold">▶</button>
                        </div>
                        {i === 0 ? <span className="absolute top-0 left-0 bg-[#8CE63C] text-[#080b08] text-[8px] font-bold px-1 rounded-br">MAIN</span> : null}
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#69776b] mt-1">Reorder with ◀ ▶, delete with ✕. Changes save when you click Save Product.</p>
                </div>
              ) : null}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Name</label>
                  <input value={editing.name} onChange={(e) => setField("name", e.target.value)} className={cx(input, "w-full")} data-testid="product-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Category</label>
                  <select value={editing.category} onChange={(e) => setField("category", e.target.value)} className={cx(input, "w-full cursor-pointer")} data-testid="product-category-input">
                    {PRODUCT_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Price (USD)</label>
                  <input type="number" value={editing.price} onChange={(e) => setField("price", e.target.value)} className={cx(input, "w-full")} data-testid="product-price-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Featured Badge</label>
                  <select value={editing.badge || ""} onChange={(e) => setField("badge", e.target.value)} className={cx(input, "w-full cursor-pointer")} data-testid="product-badge-input">
                    <option value="">None</option>
                    <option value="New">New</option>
                    <option value="Best Seller">Best Seller</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Availability</label>
                  <select value={editing.status || "available"} onChange={(e) => setField("status", e.target.value)} className={cx(input, "w-full cursor-pointer")} data-testid="product-status-input">
                    {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Description</label>
                <textarea rows={3} value={editing.description} onChange={(e) => setField("description", e.target.value)} className={cx(input, "w-full resize-y")} data-testid="product-description-input" />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Warranty Details (this model)</label>
                <textarea rows={2} value={editing.warranty || ""} onChange={(e) => setField("warranty", e.target.value)} placeholder="e.g. 1-year warranty on frame, battery and motor." className={cx(input, "w-full resize-y")} data-testid="product-warranty-input" />
              </div>

              {/* Colors */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">Colors</label>
                <div className="flex flex-wrap items-center gap-2">
                  {editing.colors.map((c, i) => (
                    <div key={i} className="flex items-center gap-1 rounded-md border border-[#232f25] bg-[#0d120e] pl-1 pr-2 py-1">
                      <input type="color" value={c} onChange={(e) => setColor(i, e.target.value)} className="h-6 w-6 bg-transparent border-0 cursor-pointer" />
                      <button onClick={() => removeColor(i)} className="text-[#FCA5A5]"><X size={13} /></button>
                    </div>
                  ))}
                  <button onClick={addColor} className="px-3 py-1.5 rounded-md border border-[#232f25] text-xs text-[#9EABA0] hover:border-[#8CE63C]/50">+ Color</button>
                </div>
              </div>

              {/* Specs */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-2">Specifications</label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SPEC_KEYS.map((k) => (
                    <div key={k}>
                      <span className="block text-[10px] uppercase tracking-wide text-[#69776b] mb-1">{k}</span>
                      <input value={editing.specs[k] || ""} onChange={(e) => setSpec(k, e.target.value)} className={cx(input, "w-full")} data-testid={`product-spec-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-[#0a0d0a]/95 backdrop-blur border-t border-[#232f25] px-6 py-4 flex items-center justify-between gap-3">
              {editing.id ? (
                <button onClick={() => remove(editing)} className="flex items-center gap-2 text-sm font-semibold text-[#FCA5A5] hover:underline">
                  <Trash2 size={15} /> Delete
                </button>
              ) : <span />}
              <div className="flex gap-2">
                <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50">Cancel</button>
                <button data-testid="product-save-button" onClick={save} disabled={saving} className="px-6 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold hover:bg-[#9df04f] transition-colors disabled:opacity-50">
                  {saving ? "Saving…" : "Save Product"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
